import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { api, apiError } from "../api/client";
import { useAuth } from "../store/auth";
import PageHeader from "../components/ui/PageHeader";
import {
  IconAccounts,
  IconActivity,
  IconAlert,
  IconCrown,
  IconShield,
  IconUsers,
} from "../components/ui/Icons";

type Tab = "overview" | "users" | "ig" | "subs" | "logs" | "diag";

export default function Admin(): JSX.Element {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>("overview");

  const tabs: { id: Tab; label: string; icon: JSX.Element }[] = [
    { id: "overview", label: t("admin.tabs.overview"), icon: <IconShield size={14} /> },
    { id: "users", label: t("admin.tabs.users"), icon: <IconUsers size={14} /> },
    { id: "ig", label: t("admin.tabs.instagramAccounts"), icon: <IconAccounts size={14} /> },
    { id: "subs", label: t("admin.tabs.subscriptions"), icon: <IconCrown size={14} /> },
    { id: "logs", label: t("admin.tabs.logs"), icon: <IconActivity size={14} /> },
    { id: "diag", label: t("admin.tabs.diagnostics"), icon: <IconAlert size={14} /> },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={t("admin.eyebrow")}
        title={t("admin.title")}
        subtitle={t("admin.subtitle")}
        icon={<IconShield />}
      />

      <div className="flex flex-wrap gap-2 border-b border-slate-800 pb-1">
        {tabs.map((it) => (
          <button
            key={it.id}
            type="button"
            onClick={() => setTab(it.id)}
            className={`inline-flex items-center gap-2 rounded-t-lg px-3 py-2 text-sm font-medium transition ${
              tab === it.id
                ? "bg-brand-500/15 text-brand-100 ring-1 ring-brand-400/30"
                : "text-slate-400 hover:text-slate-100"
            }`}
          >
            {it.icon}
            {it.label}
          </button>
        ))}
      </div>

      {tab === "overview" && <OverviewTab />}
      {tab === "users" && <UsersTab />}
      {tab === "ig" && <InstagramAccountsTab />}
      {tab === "subs" && <SubscriptionsTab />}
      {tab === "logs" && <LogsTab />}
      {tab === "diag" && <DiagnosticsTab />}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Overview                                                                  */
/* -------------------------------------------------------------------------- */

interface AdminStats {
  users: {
    total: number;
    newLast7d: number;
    byPlan: Record<string, number>;
    byCountry: { country: string; count: number }[];
  };
  subscriptions: { active: number; trialing: number; pastDue: number };
  instagramAccounts: {
    total: number;
    active: number;
    pendingLogin: number;
    challenge: number;
    banned: number;
  };
}

function OverviewTab(): JSX.Element {
  const { t } = useTranslation();
  const q = useQuery({
    queryKey: ["admin", "stats"],
    queryFn: async (): Promise<AdminStats> => {
      const { data } = await api.get("/api/admin/stats");
      return data;
    },
    refetchInterval: 30_000,
  });

  if (q.isLoading) return <p className="text-sm text-slate-400">{t("common.loading")}</p>;
  if (q.isError || !q.data) return <p className="text-sm text-rose-300">{t("admin.errors.loadFailed")}</p>;

  const s = q.data;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label={t("admin.stats.totalUsers")} value={s.users.total} />
        <Stat label={t("admin.stats.newUsers7d")} value={s.users.newLast7d} />
        <Stat label={t("admin.stats.activeSubs")} value={s.subscriptions.active} />
        <Stat label={t("admin.stats.trialingSubs")} value={s.subscriptions.trialing} />
        <Stat label={t("admin.stats.pastDueSubs")} value={s.subscriptions.pastDue} tone={s.subscriptions.pastDue > 0 ? "amber" : "default"} />
        <Stat label={t("admin.stats.totalIg")} value={s.instagramAccounts.total} />
        <Stat label={t("admin.stats.activeIg")} value={s.instagramAccounts.active} />
        <Stat label={t("admin.stats.bannedIg")} value={s.instagramAccounts.banned} tone={s.instagramAccounts.banned > 0 ? "rose" : "default"} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="card">
          <h3 className="mb-3 text-sm font-semibold text-white">{t("admin.stats.byPlanHeading")}</h3>
          <ul className="space-y-1.5 text-sm">
            {Object.entries(s.users.byPlan).map(([plan, count]) => (
              <li key={plan} className="flex items-center justify-between">
                <span className="capitalize text-slate-300">
                  {plan === "none" ? t("admin.stats.planNone") : plan}
                </span>
                <span className="font-mono text-slate-100">{count}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="card">
          <h3 className="mb-3 text-sm font-semibold text-white">
            {t("admin.stats.byCountryHeading")}
          </h3>
          {s.users.byCountry.length === 0 ? (
            <p className="text-sm text-slate-500">{t("admin.stats.noCountryData")}</p>
          ) : (
            <ul className="space-y-1.5 text-sm">
              {s.users.byCountry.map((c) => (
                <li key={c.country} className="flex items-center justify-between">
                  <span className="uppercase text-slate-300">
                    {countryFlag(c.country)} {c.country}
                  </span>
                  <span className="font-mono text-slate-100">{c.count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat
          label={t("admin.stats.pendingLoginIg")}
          value={s.instagramAccounts.pendingLogin}
          tone={s.instagramAccounts.pendingLogin > 0 ? "amber" : "default"}
        />
        <Stat
          label={t("admin.stats.challengeIg")}
          value={s.instagramAccounts.challenge}
          tone={s.instagramAccounts.challenge > 0 ? "amber" : "default"}
        />
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Users                                                                     */
/* -------------------------------------------------------------------------- */

interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: "user" | "admin";
  plan: string | null;
  subscriptionStatus: string | null;
  hasActiveSubscription: boolean;
  signupIp: string | null;
  signupCountry: string | null;
  igAccountCount: number;
  stripeCustomerId: string | null;
  createdAt: string;
}

function UsersTab(): JSX.Element {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const currentUserId = useAuth((s) => s.user?.id);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [planEditing, setPlanEditing] = useState<AdminUser | null>(null);

  const q = useQuery({
    queryKey: ["admin", "users", page, search],
    queryFn: async () => {
      const { data } = await api.get("/api/admin/users", {
        params: { page, pageSize: 25, search },
      });
      return data as { total: number; users: AdminUser[] };
    },
  });

  const roleMutation = useMutation({
    mutationFn: async (vars: { id: string; role: "user" | "admin" }) => {
      await api.post(`/api/admin/users/${vars.id}/role`, { role: vars.role });
    },
    onSuccess: () => {
      toast.success(t("admin.toast.roleUpdated"));
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
      qc.invalidateQueries({ queryKey: ["admin", "stats"] });
    },
    onError: (err) => toast.error(apiError(err)),
  });

  const locale = i18n.resolvedLanguage || i18n.language || "en";

  return (
    <div className="space-y-3">
      <input
        type="search"
        placeholder={t("admin.users.searchPlaceholder")}
        className="input"
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setPage(1);
        }}
      />

      {q.isLoading ? (
        <p className="text-sm text-slate-400">{t("common.loading")}</p>
      ) : q.isError || !q.data ? (
        <p className="text-sm text-rose-300">{t("admin.errors.loadFailed")}</p>
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-slate-800 bg-slate-950/40">
            <table className="w-full text-sm">
              <thead className="bg-slate-900/60 text-left text-[11px] uppercase tracking-wider text-slate-400">
                <tr>
                  <Th>{t("admin.users.col.user")}</Th>
                  <Th>{t("admin.users.col.country")}</Th>
                  <Th>{t("admin.users.col.plan")}</Th>
                  <Th>{t("admin.users.col.status")}</Th>
                  <Th>{t("admin.users.col.igAccounts")}</Th>
                  <Th>{t("admin.users.col.signupIp")}</Th>
                  <Th>{t("admin.users.col.createdAt")}</Th>
                  <Th>{t("admin.users.col.actions")}</Th>
                </tr>
              </thead>
              <tbody>
                {q.data.users.map((u) => {
                  const isSelf = u.id === currentUserId;
                  return (
                    <tr key={u.id} className="border-t border-slate-800/70 hover:bg-slate-900/40">
                      <Td>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-white">{u.email}</span>
                          {u.role === "admin" && (
                            <span className="pill bg-fuchsia-500/15 text-fuchsia-200 ring-1 ring-fuchsia-400/30">
                              ADMIN
                            </span>
                          )}
                          {isSelf && (
                            <span className="pill bg-slate-700/40 text-slate-300 ring-1 ring-slate-600/40">
                              {t("admin.users.you")}
                            </span>
                          )}
                        </div>
                        {u.name && <div className="text-xs text-slate-400">{u.name}</div>}
                      </Td>
                      <Td>
                        {u.signupCountry ? (
                          <span>
                            {countryFlag(u.signupCountry)}{" "}
                            <span className="uppercase">{u.signupCountry}</span>
                          </span>
                        ) : (
                          <span className="text-slate-600">—</span>
                        )}
                      </Td>
                      <Td>
                        <span className="capitalize">{u.plan || "—"}</span>
                        {u.stripeCustomerId && (
                          <span
                            className="ml-1 text-[10px] uppercase tracking-wider text-purple-300"
                            title={t("admin.users.stripeLinked")}
                          >
                            ⓢ
                          </span>
                        )}
                      </Td>
                      <Td>
                        <SubBadge status={u.subscriptionStatus} />
                      </Td>
                      <Td>{u.igAccountCount}</Td>
                      <Td>
                        <span className="font-mono text-xs text-slate-400">
                          {u.signupIp || "—"}
                        </span>
                      </Td>
                      <Td>
                        <span className="text-xs text-slate-400">
                          {new Date(u.createdAt).toLocaleDateString(locale, {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })}
                        </span>
                      </Td>
                      <Td>
                        <div className="flex flex-wrap gap-1">
                          <button
                            type="button"
                            className="btn-ghost btn-sm text-xs"
                            disabled={roleMutation.isPending || isSelf}
                            title={
                              isSelf
                                ? t("admin.users.cannotDemoteSelf")
                                : undefined
                            }
                            onClick={() => {
                              const next = u.role === "admin" ? "user" : "admin";
                              if (
                                confirm(
                                  t("admin.users.confirmRoleChange", {
                                    email: u.email,
                                    role: next,
                                  })
                                )
                              ) {
                                roleMutation.mutate({ id: u.id, role: next });
                              }
                            }}
                          >
                            {u.role === "admin"
                              ? t("admin.users.demote")
                              : t("admin.users.promote")}
                          </button>
                          <button
                            type="button"
                            className="btn-ghost btn-sm text-xs"
                            onClick={() => setPlanEditing(u)}
                          >
                            {t("admin.users.changePlan")}
                          </button>
                        </div>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <Paginator
            page={page}
            total={q.data.total}
            pageSize={25}
            onPage={setPage}
          />
        </>
      )}

      {planEditing && (
        <PlanEditModal
          user={planEditing}
          onClose={() => setPlanEditing(null)}
        />
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Plan Edit Modal                                                           */
/* -------------------------------------------------------------------------- */

function PlanEditModal({
  user,
  onClose,
}: {
  user: AdminUser;
  onClose: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [plan, setPlan] = useState<"solo" | "pro" | "label">(
    (user.plan as "solo" | "pro" | "label") || "solo"
  );
  const [years, setYears] = useState(1);

  const mutation = useMutation({
    mutationFn: async (body: { plan?: string; years?: number; revoke?: boolean }) => {
      const { data } = await api.patch(
        `/api/admin/users/${user.id}/subscription`,
        body
      );
      return data;
    },
    onSuccess: () => {
      toast.success(t("admin.toast.planUpdated"));
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
      qc.invalidateQueries({ queryKey: ["admin", "subs"] });
      qc.invalidateQueries({ queryKey: ["admin", "stats"] });
      onClose();
    },
    onError: (err) => toast.error(apiError(err)),
  });

  const stripeLinked = Boolean(user.stripeCustomerId);

  return (
    <div
      className="fixed inset-0 z-40 flex items-end justify-center bg-slate-950/70 backdrop-blur-sm sm:items-center"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-t-2xl border border-slate-800 bg-slate-900 p-5 shadow-2xl sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold text-white">
          {t("admin.planEdit.title", { email: user.email })}
        </h3>
        <p className="mt-1 text-sm text-slate-400">
          {t("admin.planEdit.subtitle")}
        </p>

        {stripeLinked && (
          <p className="mt-3 rounded-lg bg-amber-500/10 px-3 py-2 text-xs text-amber-200 ring-1 ring-amber-400/30">
            {t("admin.planEdit.stripeWarning")}
          </p>
        )}

        <div className="mt-4 space-y-3">
          <div>
            <label className="label">{t("admin.planEdit.planLabel")}</label>
            <select
              className="input"
              value={plan}
              onChange={(e) => setPlan(e.target.value as "solo" | "pro" | "label")}
            >
              <option value="solo">Solo</option>
              <option value="pro">Pro</option>
              <option value="label">Label</option>
            </select>
          </div>

          <div>
            <label className="label">{t("admin.planEdit.yearsLabel")}</label>
            <input
              type="number"
              className="input"
              min={1}
              max={50}
              value={years}
              onChange={(e) => setYears(Number(e.target.value))}
            />
            <p className="mt-1 text-xs text-slate-500">
              {t("admin.planEdit.yearsHint")}
            </p>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap items-center justify-between gap-2">
          <button
            type="button"
            className="btn-ghost btn-sm text-rose-300"
            disabled={mutation.isPending || stripeLinked}
            onClick={() => {
              if (confirm(t("admin.planEdit.confirmRevoke", { email: user.email }))) {
                mutation.mutate({ revoke: true });
              }
            }}
          >
            {t("admin.planEdit.revoke")}
          </button>
          <div className="flex gap-2">
            <button type="button" className="btn-ghost" onClick={onClose}>
              {t("common.cancel")}
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={mutation.isPending || stripeLinked}
              onClick={() => mutation.mutate({ plan, years })}
            >
              {mutation.isPending
                ? t("admin.planEdit.saving")
                : t("admin.planEdit.save")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Instagram Accounts                                                        */
/* -------------------------------------------------------------------------- */

interface AdminIgAccount {
  id: string;
  userId: string;
  ownerEmail: string | null;
  ownerPlan: string | null;
  ownerSubscriptionStatus: string | null;
  igUsername: string;
  status: string;
  statusReason: string;
  followerCount: number;
  proxyCountry: string;
  consecutiveFailures: number;
  lastLoggedInAt: string | null;
  createdAt: string;
  bandwidthDate: string;
  bandwidthBytes: number;
  bandwidthBytesLifetime: number;
  bytesUsedToday: number;
  bandwidthCapExceeded: boolean;
  lastExitIp: string;
  lastExitIpAt: string | null;
}

function formatMb(bytes: number): string {
  if (!bytes) return "0 MB";
  const mb = bytes / (1024 * 1024);
  if (mb < 1) return `${(bytes / 1024).toFixed(0)} KB`;
  if (mb < 100) return `${mb.toFixed(1)} MB`;
  if (mb < 1024) return `${mb.toFixed(0)} MB`;
  return `${(mb / 1024).toFixed(2)} GB`;
}

function InstagramAccountsTab(): JSX.Element {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [targetsFor, setTargetsFor] = useState<AdminIgAccount | null>(null);

  const q = useQuery({
    queryKey: ["admin", "ig", page, search, statusFilter],
    queryFn: async () => {
      const { data } = await api.get("/api/admin/instagram-accounts", {
        params: { page, pageSize: 25, search, status: statusFilter },
      });
      return data as {
        total: number;
        bandwidthDailyCapBytes: number;
        accounts: AdminIgAccount[];
      };
    },
  });

  const suspendMutation = useMutation({
    mutationFn: async (vars: { id: string; action: "suspend" | "unsuspend"; reason?: string }) => {
      await api.post(
        `/api/admin/instagram-accounts/${vars.id}/${vars.action}`,
        vars.reason ? { reason: vars.reason } : {}
      );
    },
    onSuccess: () => {
      toast.success(t("admin.toast.suspendUpdated"));
      qc.invalidateQueries({ queryKey: ["admin", "ig"] });
      qc.invalidateQueries({ queryKey: ["admin", "stats"] });
    },
    onError: (err) => toast.error(apiError(err)),
  });

  const resetBandwidthMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.post(`/api/admin/instagram-accounts/${id}/reset-bandwidth`);
    },
    onSuccess: () => {
      toast.success(t("admin.toast.bandwidthReset"));
      qc.invalidateQueries({ queryKey: ["admin", "ig"] });
    },
    onError: (err) => toast.error(apiError(err)),
  });

  const resetStateMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.post(`/api/admin/instagram-accounts/${id}/reset-state`);
    },
    onSuccess: () => {
      toast.success(t("admin.toast.stateReset"));
      qc.invalidateQueries({ queryKey: ["admin", "ig"] });
    },
    onError: (err) => toast.error(apiError(err)),
  });

  const locale = i18n.resolvedLanguage || i18n.language || "en";

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <input
          type="search"
          placeholder={t("admin.ig.searchPlaceholder")}
          className="input flex-1"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
        <select
          className="input sm:w-56"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">{t("admin.ig.allStatuses")}</option>
          {[
            "active",
            "paused",
            "pending_login",
            "awaiting_2fa_code",
            "challenge_required",
            "verifying",
            "error",
            "banned",
          ].map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      {q.isLoading ? (
        <p className="text-sm text-slate-400">{t("common.loading")}</p>
      ) : q.isError || !q.data ? (
        <p className="text-sm text-rose-300">{t("admin.errors.loadFailed")}</p>
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-slate-800 bg-slate-950/40">
            <table className="w-full text-sm">
              <thead className="bg-slate-900/60 text-left text-[11px] uppercase tracking-wider text-slate-400">
                <tr>
                  <Th>{t("admin.ig.col.username")}</Th>
                  <Th>{t("admin.ig.col.owner")}</Th>
                  <Th>{t("admin.ig.col.status")}</Th>
                  <Th>{t("admin.ig.col.country")}</Th>
                  <Th>{t("admin.ig.col.followers")}</Th>
                  <Th>{t("admin.ig.col.failures")}</Th>
                  <Th>{t("admin.ig.col.bandwidth")}</Th>
                  <Th>{t("admin.ig.col.lastLogin")}</Th>
                  <Th>{t("admin.ig.col.actions")}</Th>
                </tr>
              </thead>
              <tbody>
                {q.data.accounts.map((a) => {
                  const isAdminSuspended =
                    a.status === "paused" &&
                    (a.statusReason || "").toLowerCase().startsWith("suspended by admin");
                  return (
                    <tr key={a.id} className="border-t border-slate-800/70 hover:bg-slate-900/40">
                      <Td>
                        <button
                          type="button"
                          className="font-medium text-white hover:text-brand-300"
                          onClick={() => setTargetsFor(a)}
                          title={t("admin.ig.viewTargetsTitle")}
                        >
                          @{a.igUsername}
                        </button>
                        <div className="text-xs text-slate-500">
                          {new Date(a.createdAt).toLocaleDateString(locale, {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })}
                        </div>
                      </Td>
                      <Td>
                        <div className="text-xs text-slate-300">{a.ownerEmail || "—"}</div>
                        <div className="text-xs text-slate-500 capitalize">
                          {a.ownerPlan || "—"}
                        </div>
                      </Td>
                      <Td>
                        <IgStatusBadge status={a.status} />
                        {a.statusReason && (
                          <div className="text-[11px] text-slate-500" title={a.statusReason}>
                            {a.statusReason.length > 30
                              ? a.statusReason.slice(0, 30) + "…"
                              : a.statusReason}
                          </div>
                        )}
                      </Td>
                      <Td>
                        <span className="uppercase">{a.proxyCountry || "—"}</span>
                      </Td>
                      <Td>{a.followerCount.toLocaleString()}</Td>
                      <Td>
                        <span
                          className={
                            a.consecutiveFailures > 0
                              ? "text-amber-300"
                              : "text-slate-400"
                          }
                        >
                          {a.consecutiveFailures}
                        </span>
                      </Td>
                      <Td>
                        {(() => {
                          const cap = q.data!.bandwidthDailyCapBytes;
                          const used = a.bytesUsedToday;
                          const pct = cap > 0 ? (used / cap) * 100 : 0;
                          const tone = a.bandwidthCapExceeded
                            ? "text-rose-300"
                            : pct >= 80
                            ? "text-amber-300"
                            : "text-slate-400";
                          return (
                            <div className="space-y-0.5">
                              <div className={`text-xs font-medium ${tone}`}>
                                {formatMb(used)}
                                {cap > 0 && (
                                  <span className="text-slate-500">
                                    {" / "}
                                    {formatMb(cap)}
                                  </span>
                                )}
                              </div>
                              <div className="text-[10px] text-slate-500">
                                {t("admin.ig.col.bandwidthLifetime", {
                                  total: formatMb(a.bandwidthBytesLifetime),
                                })}
                              </div>
                            </div>
                          );
                        })()}
                      </Td>
                      <Td>
                        <span className="text-xs text-slate-400">
                          {a.lastLoggedInAt
                            ? new Date(a.lastLoggedInAt).toLocaleString(locale)
                            : "—"}
                        </span>
                      </Td>
                      <Td>
                        {(["verifying", "awaiting_2fa_code", "challenge_required", "error", "pending_login"] as const).includes(
                          a.status as any
                        ) ? (
                          <button
                            type="button"
                            className="btn-ghost btn-sm mr-2 text-xs text-amber-300"
                            disabled={resetStateMutation.isPending}
                            onClick={() => {
                              if (
                                confirm(
                                  t("admin.ig.confirmResetState", {
                                    username: a.igUsername,
                                    status: a.status,
                                  })
                                )
                              ) {
                                resetStateMutation.mutate(a.id);
                              }
                            }}
                          >
                            {t("admin.ig.resetState")}
                          </button>
                        ) : null}
                        {a.bandwidthCapExceeded || a.bytesUsedToday > 0 ? (
                          <button
                            type="button"
                            className="btn-ghost btn-sm mr-2 text-xs text-sky-300"
                            disabled={resetBandwidthMutation.isPending}
                            onClick={() => {
                              if (
                                confirm(
                                  t("admin.ig.confirmResetBandwidth", {
                                    username: a.igUsername,
                                    used: formatMb(a.bytesUsedToday),
                                  })
                                )
                              ) {
                                resetBandwidthMutation.mutate(a.id);
                              }
                            }}
                          >
                            {t("admin.ig.resetBandwidth")}
                          </button>
                        ) : null}
                        {isAdminSuspended ? (
                          <button
                            type="button"
                            className="btn-ghost btn-sm text-xs text-emerald-300"
                            disabled={suspendMutation.isPending}
                            onClick={() => {
                              if (
                                confirm(
                                  t("admin.ig.confirmUnsuspend", {
                                    username: a.igUsername,
                                  })
                                )
                              ) {
                                suspendMutation.mutate({ id: a.id, action: "unsuspend" });
                              }
                            }}
                          >
                            {t("admin.ig.unsuspend")}
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="btn-ghost btn-sm text-xs text-rose-300"
                            disabled={
                              suspendMutation.isPending ||
                              a.status === "banned" ||
                              a.status === "paused"
                            }
                            title={
                              a.status === "banned"
                                ? t("admin.ig.alreadyBanned")
                                : a.status === "paused"
                                ? t("admin.ig.alreadyPaused")
                                : undefined
                            }
                            onClick={() => {
                              const reason = prompt(
                                t("admin.ig.suspendReasonPrompt", {
                                  username: a.igUsername,
                                })
                              );
                              if (reason !== null) {
                                suspendMutation.mutate({
                                  id: a.id,
                                  action: "suspend",
                                  reason: reason || undefined,
                                });
                              }
                            }}
                          >
                            {t("admin.ig.suspend")}
                          </button>
                        )}
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <Paginator
            page={page}
            total={q.data.total}
            pageSize={25}
            onPage={setPage}
          />
        </>
      )}

      {targetsFor && (
        <TargetsModal
          account={targetsFor}
          onClose={() => setTargetsFor(null)}
        />
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Targets Modal — inspect an account's scraped target queue                 */
/* -------------------------------------------------------------------------- */

interface AdminTargetsResponse {
  account: { id: string; igUsername: string; userId: string };
  campaigns: {
    id: string;
    name: string;
    enabled: boolean;
    sourceCount: number;
  }[];
  perSource: {
    campaignId: string | null;
    campaignName: string | null;
    campaignEnabled: boolean | null;
    source: string;
    sourceValue: string;
    total: number;
    pending: number;
    processed: number;
    blocked: number;
    lastAdded: string | null;
    lastInteractedAt: string | null;
  }[];
  recent: {
    page: number;
    pageSize: number;
    total: number;
    targets: {
      id: string;
      campaignId: string | null;
      source: string;
      sourceValue: string;
      targetUsername: string;
      addedAt: string;
      interactedAt: string | null;
      skipReason: string;
      alreadyFollowsMe: boolean;
      status: "pending" | "processed" | "blocked";
    }[];
  };
  scrapeRuns: {
    type: string;
    day: string;
    runs: number;
    success: number;
    failed: number;
    collected: number;
    lastRun: string;
  }[];
}

function TargetsModal({
  account,
  onClose,
}: {
  account: AdminIgAccount;
  onClose: () => void;
}): JSX.Element {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage || i18n.language || "en";
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<
    "" | "pending" | "processed" | "blocked"
  >("");
  const [campaignFilter, setCampaignFilter] = useState("");
  const [search, setSearch] = useState("");

  const q = useQuery({
    queryKey: [
      "admin",
      "ig",
      account.id,
      "targets",
      page,
      statusFilter,
      campaignFilter,
      search,
    ],
    queryFn: async () => {
      const { data } = await api.get(
        `/api/admin/instagram-accounts/${account.id}/targets`,
        {
          params: {
            page,
            pageSize: 50,
            status: statusFilter,
            campaignId: campaignFilter || undefined,
            search: search.trim() || undefined,
          },
        }
      );
      return data as AdminTargetsResponse;
    },
  });

  return (
    <div
      className="fixed inset-0 z-40 flex items-end justify-center bg-slate-950/70 backdrop-blur-sm sm:items-center"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="flex max-h-[92vh] w-full max-w-5xl flex-col rounded-t-2xl border border-slate-800 bg-slate-900 shadow-2xl sm:max-h-[88vh] sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-slate-800 p-4">
          <div>
            <h3 className="text-lg font-semibold text-white">
              {t("admin.targets.title", { username: account.igUsername })}
            </h3>
            <p className="mt-0.5 text-xs text-slate-400">
              {t("admin.targets.subtitle")}
            </p>
          </div>
          <button
            type="button"
            className="btn-ghost btn-sm"
            onClick={onClose}
            aria-label={t("common.close")}
          >
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {q.isLoading ? (
            <p className="text-sm text-slate-400">{t("common.loading")}</p>
          ) : q.isError || !q.data ? (
            <p className="text-sm text-rose-300">
              {t("admin.errors.loadFailed")}
            </p>
          ) : (
            <div className="space-y-5">
              {/* Scrape activity (last 7 days) */}
              <section>
                <h4 className="mb-2 text-sm font-semibold text-slate-200">
                  {t("admin.targets.scrapeActivity")}
                </h4>
                {q.data.scrapeRuns.length === 0 ? (
                  <p className="rounded-lg bg-amber-500/10 px-3 py-2 text-xs text-amber-200 ring-1 ring-amber-400/30">
                    {t("admin.targets.noScrape7d")}
                  </p>
                ) : (
                  <div className="overflow-x-auto rounded-lg border border-slate-800">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-900/60 text-left uppercase tracking-wider text-slate-500">
                        <tr>
                          <Th>{t("admin.targets.col.day")}</Th>
                          <Th>{t("admin.targets.col.type")}</Th>
                          <Th>{t("admin.targets.col.runs")}</Th>
                          <Th>{t("admin.targets.col.success")}</Th>
                          <Th>{t("admin.targets.col.failed")}</Th>
                          <Th>{t("admin.targets.col.collected")}</Th>
                          <Th>{t("admin.targets.col.lastRun")}</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {q.data.scrapeRuns.map((r, i) => (
                          <tr
                            key={`${r.day}-${r.type}-${i}`}
                            className="border-t border-slate-800/70"
                          >
                            <Td>{r.day}</Td>
                            <Td>
                              <span className="font-mono text-slate-300">
                                {r.type}
                              </span>
                            </Td>
                            <Td>{r.runs}</Td>
                            <Td>
                              <span className="text-emerald-300">
                                {r.success}
                              </span>
                            </Td>
                            <Td>
                              <span
                                className={
                                  r.failed > 0 ? "text-rose-300" : "text-slate-500"
                                }
                              >
                                {r.failed}
                              </span>
                            </Td>
                            <Td>
                              <span className="font-medium text-slate-200">
                                {r.collected}
                              </span>
                            </Td>
                            <Td>
                              <span className="text-slate-500">
                                {new Date(r.lastRun).toLocaleString(locale)}
                              </span>
                            </Td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              {/* Per-source breakdown */}
              <section>
                <h4 className="mb-2 text-sm font-semibold text-slate-200">
                  {t("admin.targets.perSource")}
                </h4>
                {q.data.perSource.length === 0 ? (
                  <p className="rounded-lg bg-slate-800/60 px-3 py-2 text-xs text-slate-400">
                    {t("admin.targets.noTargets")}
                  </p>
                ) : (
                  <div className="overflow-x-auto rounded-lg border border-slate-800">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-900/60 text-left uppercase tracking-wider text-slate-500">
                        <tr>
                          <Th>{t("admin.targets.col.campaign")}</Th>
                          <Th>{t("admin.targets.col.source")}</Th>
                          <Th>{t("admin.targets.col.pending")}</Th>
                          <Th>{t("admin.targets.col.processed")}</Th>
                          <Th>{t("admin.targets.col.blocked")}</Th>
                          <Th>{t("admin.targets.col.total")}</Th>
                          <Th>{t("admin.targets.col.lastAdded")}</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {q.data.perSource.map((r, i) => (
                          <tr
                            key={`${r.campaignId}-${r.source}-${r.sourceValue}-${i}`}
                            className="border-t border-slate-800/70"
                          >
                            <Td>
                              <div className="flex flex-col">
                                <span className="text-slate-300">
                                  {r.campaignName || (
                                    <em className="text-slate-500">
                                      {t("admin.targets.orphan")}
                                    </em>
                                  )}
                                </span>
                                {r.campaignEnabled === false && (
                                  <span className="text-[10px] text-amber-400">
                                    {t("admin.targets.disabled")}
                                  </span>
                                )}
                              </div>
                            </Td>
                            <Td>
                              <span className="font-mono">
                                {r.source === "hashtag"
                                  ? `#${r.sourceValue}`
                                  : r.source === "account_followers"
                                  ? `@${r.sourceValue}`
                                  : r.sourceValue}
                              </span>
                              <div className="text-[10px] text-slate-500">
                                {r.source}
                              </div>
                            </Td>
                            <Td>
                              <span
                                className={
                                  r.pending === 0
                                    ? "text-rose-300"
                                    : "text-emerald-300"
                                }
                              >
                                {r.pending.toLocaleString()}
                              </span>
                            </Td>
                            <Td>{r.processed.toLocaleString()}</Td>
                            <Td>
                              <span
                                className="text-slate-400"
                                title={t("admin.targets.blockedHint")}
                              >
                                {r.blocked.toLocaleString()}
                              </span>
                            </Td>
                            <Td>
                              <span className="font-medium text-slate-200">
                                {r.total.toLocaleString()}
                              </span>
                            </Td>
                            <Td>
                              <span className="text-slate-500">
                                {r.lastAdded
                                  ? new Date(r.lastAdded).toLocaleString(locale)
                                  : "—"}
                              </span>
                            </Td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <p className="mt-1 text-[11px] text-slate-500">
                  {t("admin.targets.scrapeLimitHint")}
                </p>
              </section>

              {/* Recent targets with filters */}
              <section>
                <div className="mb-2 flex flex-wrap items-end justify-between gap-2">
                  <h4 className="text-sm font-semibold text-slate-200">
                    {t("admin.targets.recentTitle", {
                      total: q.data.recent.total.toLocaleString(),
                    })}
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    <input
                      type="search"
                      placeholder={t("admin.targets.searchPlaceholder")}
                      className="input h-8 text-xs"
                      value={search}
                      onChange={(e) => {
                        setSearch(e.target.value);
                        setPage(1);
                      }}
                    />
                    <select
                      className="input h-8 text-xs"
                      value={statusFilter}
                      onChange={(e) => {
                        setStatusFilter(
                          e.target.value as
                            | ""
                            | "pending"
                            | "processed"
                            | "blocked"
                        );
                        setPage(1);
                      }}
                    >
                      <option value="">
                        {t("admin.targets.filter.allStatuses")}
                      </option>
                      <option value="pending">
                        {t("admin.targets.filter.pending")}
                      </option>
                      <option value="processed">
                        {t("admin.targets.filter.processed")}
                      </option>
                      <option value="blocked">
                        {t("admin.targets.filter.blocked")}
                      </option>
                    </select>
                    <select
                      className="input h-8 text-xs"
                      value={campaignFilter}
                      onChange={(e) => {
                        setCampaignFilter(e.target.value);
                        setPage(1);
                      }}
                    >
                      <option value="">
                        {t("admin.targets.filter.allCampaigns")}
                      </option>
                      {q.data.campaigns.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                          {c.enabled ? "" : " (off)"}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {q.data.recent.targets.length === 0 ? (
                  <p className="rounded-lg bg-slate-800/60 px-3 py-2 text-xs text-slate-400">
                    {t("admin.targets.noRows")}
                  </p>
                ) : (
                  <div className="overflow-x-auto rounded-lg border border-slate-800">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-900/60 text-left uppercase tracking-wider text-slate-500">
                        <tr>
                          <Th>{t("admin.targets.col.username")}</Th>
                          <Th>{t("admin.targets.col.via")}</Th>
                          <Th>{t("admin.targets.col.status")}</Th>
                          <Th>{t("admin.targets.col.added")}</Th>
                          <Th>{t("admin.targets.col.interacted")}</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {q.data.recent.targets.map((t0) => (
                          <tr
                            key={t0.id}
                            className="border-t border-slate-800/70"
                          >
                            <Td>
                              <a
                                href={`https://www.instagram.com/${t0.targetUsername}/`}
                                target="_blank"
                                rel="noreferrer"
                                className="font-medium text-white hover:text-brand-300"
                              >
                                @{t0.targetUsername}
                              </a>
                            </Td>
                            <Td>
                              <span className="font-mono text-slate-400">
                                {t0.source === "hashtag"
                                  ? `#${t0.sourceValue}`
                                  : `@${t0.sourceValue}`}
                              </span>
                            </Td>
                            <Td>
                              <TargetStatusBadge
                                status={t0.status}
                                skipReason={t0.skipReason}
                              />
                            </Td>
                            <Td>
                              <span className="text-slate-500">
                                {new Date(t0.addedAt).toLocaleString(locale)}
                              </span>
                            </Td>
                            <Td>
                              <span className="text-slate-500">
                                {t0.interactedAt
                                  ? new Date(t0.interactedAt).toLocaleString(
                                      locale
                                    )
                                  : "—"}
                              </span>
                            </Td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="mt-3">
                  <Paginator
                    page={page}
                    total={q.data.recent.total}
                    pageSize={50}
                    onPage={setPage}
                  />
                </div>
              </section>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TargetStatusBadge({
  status,
  skipReason,
}: {
  status: "pending" | "processed" | "blocked";
  skipReason: string;
}): JSX.Element {
  const { t } = useTranslation();
  if (status === "pending") {
    return (
      <span className="pill bg-emerald-500/15 text-emerald-200 ring-1 ring-emerald-400/30">
        {t("admin.targets.filter.pending")}
      </span>
    );
  }
  if (status === "blocked") {
    return (
      <span
        className="pill bg-slate-700/40 text-slate-300 ring-1 ring-slate-500/30"
        title={t("admin.targets.blockedHint")}
      >
        {t("admin.targets.filter.blocked")}
      </span>
    );
  }
  return (
    <span
      className="pill bg-sky-500/15 text-sky-200 ring-1 ring-sky-400/30"
      title={skipReason || undefined}
    >
      {skipReason
        ? `${t("admin.targets.filter.processed")} · ${skipReason}`
        : t("admin.targets.filter.processed")}
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/*  Subscriptions                                                             */
/* -------------------------------------------------------------------------- */

interface AdminSubscription {
  userId: string;
  email: string;
  plan: string | null;
  subscriptionStatus: string | null;
  currentPeriodEnd: string | null;
  trialEndsAt: string | null;
  cancelAtPeriodEnd: boolean;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  createdAt: string;
}

function SubscriptionsTab(): JSX.Element {
  const { t, i18n } = useTranslation();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");

  const q = useQuery({
    queryKey: ["admin", "subs", page, statusFilter],
    queryFn: async () => {
      const { data } = await api.get("/api/admin/subscriptions", {
        params: { page, pageSize: 25, status: statusFilter },
      });
      return data as { total: number; subscriptions: AdminSubscription[] };
    },
  });

  const locale = i18n.resolvedLanguage || i18n.language || "en";

  return (
    <div className="space-y-3">
      <select
        className="input sm:w-56"
        value={statusFilter}
        onChange={(e) => {
          setStatusFilter(e.target.value);
          setPage(1);
        }}
      >
        <option value="">{t("admin.subs.allStatuses")}</option>
        {["trialing", "active", "past_due", "canceled", "unpaid", "incomplete", "incomplete_expired"].map(
          (s) => (
            <option key={s} value={s}>
              {s}
            </option>
          )
        )}
      </select>

      {q.isLoading ? (
        <p className="text-sm text-slate-400">{t("common.loading")}</p>
      ) : q.isError || !q.data ? (
        <p className="text-sm text-rose-300">{t("admin.errors.loadFailed")}</p>
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-slate-800 bg-slate-950/40">
            <table className="w-full text-sm">
              <thead className="bg-slate-900/60 text-left text-[11px] uppercase tracking-wider text-slate-400">
                <tr>
                  <Th>{t("admin.subs.col.user")}</Th>
                  <Th>{t("admin.subs.col.plan")}</Th>
                  <Th>{t("admin.subs.col.status")}</Th>
                  <Th>{t("admin.subs.col.periodEnd")}</Th>
                  <Th>{t("admin.subs.col.cancel")}</Th>
                  <Th>{t("admin.subs.col.stripeId")}</Th>
                </tr>
              </thead>
              <tbody>
                {q.data.subscriptions.map((s) => (
                  <tr key={s.userId} className="border-t border-slate-800/70 hover:bg-slate-900/40">
                    <Td>
                      <div className="font-medium text-white">{s.email}</div>
                    </Td>
                    <Td>
                      <span className="capitalize">{s.plan || "—"}</span>
                    </Td>
                    <Td>
                      <SubBadge status={s.subscriptionStatus} />
                    </Td>
                    <Td>
                      <span className="text-xs text-slate-400">
                        {s.currentPeriodEnd
                          ? new Date(s.currentPeriodEnd).toLocaleDateString(locale, {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            })
                          : "—"}
                      </span>
                      {s.trialEndsAt && (
                        <div className="text-[11px] text-emerald-300">
                          {t("admin.subs.trialEnds", {
                            date: new Date(s.trialEndsAt).toLocaleDateString(locale, {
                              month: "short",
                              day: "numeric",
                            }),
                          })}
                        </div>
                      )}
                    </Td>
                    <Td>
                      {s.cancelAtPeriodEnd ? (
                        <span className="pill bg-amber-500/15 text-amber-200 ring-1 ring-amber-400/30">
                          {t("admin.subs.cancelScheduled")}
                        </span>
                      ) : (
                        <span className="text-slate-600">—</span>
                      )}
                    </Td>
                    <Td>
                      <span className="font-mono text-[11px] text-slate-500">
                        {s.stripeSubscriptionId || s.stripeCustomerId || "—"}
                      </span>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Paginator
            page={page}
            total={q.data.total}
            pageSize={25}
            onPage={setPage}
          />
        </>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Logs                                                                      */
/* -------------------------------------------------------------------------- */

interface AdminJobRun {
  id: string;
  igAccountId: string;
  igUsername: string | null;
  type: string;
  status: "queued" | "running" | "success" | "failed" | "skipped";
  actionsPerformed: number;
  error: string;
  errorKind: string;
  attempts: number;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
}

const JOB_TYPES = [
  "login",
  "scrape_followers",
  "scrape_hashtag",
  "view_stories",
  "like_stories",
  "follow",
  "unfollow",
  "comment",
  "send_dm",
  "new_follower_watcher",
  "daily_metric_snapshot",
] as const;

function LogsTab(): JSX.Element {
  const { t, i18n } = useTranslation();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [accountFilter, setAccountFilter] = useState("");

  const q = useQuery({
    queryKey: ["admin", "logs", page, statusFilter, typeFilter, accountFilter],
    queryFn: async () => {
      const { data } = await api.get("/api/admin/job-runs", {
        params: {
          page,
          pageSize: 50,
          status: statusFilter,
          type: typeFilter,
          igUsername: accountFilter,
        },
      });
      return data as { total: number; runs: AdminJobRun[] };
    },
    // Auto-refresh every 15s — workers are constantly producing new rows and
    // staring at a static view in the middle of an incident is frustrating.
    refetchInterval: 15_000,
  });

  const locale = i18n.resolvedLanguage || i18n.language || "en";

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <input
          type="search"
          placeholder={t("admin.logs.accountPlaceholder")}
          className="input sm:w-48"
          value={accountFilter}
          onChange={(e) => {
            setAccountFilter(e.target.value);
            setPage(1);
          }}
        />
        <select
          className="input sm:w-48"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">{t("admin.logs.allStatuses")}</option>
          {(["queued", "running", "success", "failed", "skipped"] as const).map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select
          className="input sm:w-56"
          value={typeFilter}
          onChange={(e) => {
            setTypeFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">{t("admin.logs.allTypes")}</option>
          {JOB_TYPES.map((tp) => (
            <option key={tp} value={tp}>
              {tp}
            </option>
          ))}
        </select>
        <span className="text-xs text-slate-500 sm:self-center">
          {t("admin.logs.autoRefresh")}
        </span>
      </div>

      {q.isLoading ? (
        <p className="text-sm text-slate-400">{t("common.loading")}</p>
      ) : q.isError || !q.data ? (
        <p className="text-sm text-rose-300">{t("admin.errors.loadFailed")}</p>
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-slate-800 bg-slate-950/40">
            <table className="w-full text-sm">
              <thead className="bg-slate-900/60 text-left text-[11px] uppercase tracking-wider text-slate-400">
                <tr>
                  <Th>{t("admin.logs.col.when")}</Th>
                  <Th>{t("admin.logs.col.account")}</Th>
                  <Th>{t("admin.logs.col.type")}</Th>
                  <Th>{t("admin.logs.col.status")}</Th>
                  <Th>{t("admin.logs.col.actions")}</Th>
                  <Th>{t("admin.logs.col.attempts")}</Th>
                  <Th>{t("admin.logs.col.error")}</Th>
                </tr>
              </thead>
              <tbody>
                {q.data.runs.map((r) => (
                  <tr key={r.id} className="border-t border-slate-800/70 hover:bg-slate-900/40">
                    <Td>
                      <span className="text-xs text-slate-400">
                        {new Date(r.createdAt).toLocaleString(locale, {
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit",
                        })}
                      </span>
                    </Td>
                    <Td>
                      {r.igUsername ? (
                        <span className="font-medium text-white">@{r.igUsername}</span>
                      ) : (
                        <span className="text-slate-600">—</span>
                      )}
                    </Td>
                    <Td>
                      <span className="font-mono text-xs">{r.type}</span>
                    </Td>
                    <Td>
                      <JobStatusBadge status={r.status} />
                    </Td>
                    <Td>{r.actionsPerformed}</Td>
                    <Td>
                      <span
                        className={
                          r.attempts > 1 ? "text-amber-300" : "text-slate-400"
                        }
                      >
                        {r.attempts}
                      </span>
                    </Td>
                    <Td>
                      {r.error ? (
                        <details>
                          <summary className="cursor-pointer text-xs text-rose-300">
                            {r.errorKind || t("admin.logs.error")}
                          </summary>
                          <pre className="mt-1 max-w-md whitespace-pre-wrap break-words rounded bg-slate-950 p-2 text-[10px] text-slate-300">
                            {r.error}
                          </pre>
                        </details>
                      ) : (
                        <span className="text-slate-600">—</span>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Paginator
            page={page}
            total={q.data.total}
            pageSize={50}
            onPage={setPage}
          />
        </>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Diagnostics                                                               */
/* -------------------------------------------------------------------------- */

interface DiagnosticEventRow {
  id: string;
  igAccountId: string | null;
  igUsername: string | null;
  jobId: string;
  jobType: string;
  level: "info" | "warn" | "error";
  event: string;
  message: string;
  context: Record<string, unknown>;
  errorKind: string;
  url: string;
  hasScreenshot: boolean;
  screenshotId: string | null;
  createdAt: string;
}

function DiagnosticsTab(): JSX.Element {
  const { t, i18n } = useTranslation();
  const [page, setPage] = useState(1);
  const [levelFilter, setLevelFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [accountFilter, setAccountFilter] = useState("");
  const [eventFilter, setEventFilter] = useState("");
  const [sinceHours, setSinceHours] = useState("");

  const q = useQuery({
    queryKey: [
      "admin",
      "diagnostics",
      page,
      levelFilter,
      typeFilter,
      accountFilter,
      eventFilter,
      sinceHours,
    ],
    queryFn: async () => {
      const { data } = await api.get("/api/admin/diagnostics", {
        params: {
          page,
          pageSize: 50,
          level: levelFilter,
          jobType: typeFilter,
          igUsername: accountFilter,
          event: eventFilter,
          sinceHours,
        },
      });
      return data as { total: number; events: DiagnosticEventRow[] };
    },
    refetchInterval: 15_000,
  });

  const locale = i18n.resolvedLanguage || i18n.language || "en";

  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-500">{t("admin.diagnostics.retentionNote")}</p>
      <div className="flex flex-wrap gap-2">
        <input
          type="search"
          placeholder={t("admin.logs.accountPlaceholder")}
          className="input sm:w-40"
          value={accountFilter}
          onChange={(e) => {
            setAccountFilter(e.target.value);
            setPage(1);
          }}
        />
        <input
          type="search"
          placeholder={t("admin.diagnostics.eventPlaceholder")}
          className="input sm:w-40"
          value={eventFilter}
          onChange={(e) => {
            setEventFilter(e.target.value);
            setPage(1);
          }}
        />
        <select
          className="input sm:w-36"
          value={levelFilter}
          onChange={(e) => {
            setLevelFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">{t("admin.diagnostics.allLevels")}</option>
          {(["info", "warn", "error"] as const).map((l) => (
            <option key={l} value={l}>
              {l}
            </option>
          ))}
        </select>
        <select
          className="input sm:w-48"
          value={typeFilter}
          onChange={(e) => {
            setTypeFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">{t("admin.logs.allTypes")}</option>
          {JOB_TYPES.map((tp) => (
            <option key={tp} value={tp}>
              {tp}
            </option>
          ))}
        </select>
        <select
          className="input sm:w-40"
          value={sinceHours}
          onChange={(e) => {
            setSinceHours(e.target.value);
            setPage(1);
          }}
        >
          <option value="">{t("admin.diagnostics.anyTime")}</option>
          <option value="1">{t("admin.diagnostics.lastHour")}</option>
          <option value="24">{t("admin.diagnostics.last24h")}</option>
          <option value="168">{t("admin.diagnostics.last7d")}</option>
        </select>
        <span className="text-xs text-slate-500 sm:self-center">
          {t("admin.logs.autoRefresh")}
        </span>
      </div>

      {q.isLoading ? (
        <p className="text-sm text-slate-400">{t("common.loading")}</p>
      ) : q.isError || !q.data ? (
        <p className="text-sm text-rose-300">{t("admin.errors.loadFailed")}</p>
      ) : q.data.events.length === 0 ? (
        <p className="text-sm text-slate-400">{t("admin.diagnostics.empty")}</p>
      ) : (
        <>
          <div className="space-y-2">
            {q.data.events.map((ev) => (
              <DiagnosticRow key={ev.id} ev={ev} locale={locale} />
            ))}
          </div>
          <Paginator page={page} total={q.data.total} pageSize={50} onPage={setPage} />
        </>
      )}
    </div>
  );
}

function DiagnosticRow({
  ev,
  locale,
}: {
  ev: DiagnosticEventRow;
  locale: string;
}): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const levelCls =
    ev.level === "error"
      ? "border-rose-500/30 bg-rose-500/5"
      : ev.level === "warn"
      ? "border-amber-500/30 bg-amber-500/5"
      : "border-slate-800 bg-slate-950/40";
  const hasDetail =
    ev.context && Object.keys(ev.context).length > 0
      ? true
      : ev.url || ev.hasScreenshot;

  return (
    <div className={`rounded-xl border p-3 ${levelCls}`}>
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <DiagLevelBadge level={ev.level} />
          <span className="font-mono text-xs text-brand-200">{ev.event}</span>
          {ev.igUsername && (
            <span className="text-xs font-medium text-white">@{ev.igUsername}</span>
          )}
          {ev.jobType && (
            <span className="font-mono text-[10px] text-slate-500">{ev.jobType}</span>
          )}
        </div>
        <span className="text-xs text-slate-400">
          {new Date(ev.createdAt).toLocaleString(locale, {
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          })}
        </span>
      </div>
      {ev.message && (
        <p className="mt-1 break-words text-sm text-slate-200">{ev.message}</p>
      )}
      {ev.errorKind && (
        <span className="mt-1 inline-block font-mono text-[10px] text-rose-300">
          {ev.errorKind}
        </span>
      )}
      {hasDetail && (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="mt-2 text-xs text-slate-400 underline-offset-2 hover:text-slate-100 hover:underline"
        >
          {open ? t("admin.diagnostics.hideDetails") : t("admin.diagnostics.showDetails")}
        </button>
      )}
      {open && (
        <div className="mt-2 space-y-2">
          {ev.url && (
            <p className="break-all text-[10px] text-slate-500">{ev.url}</p>
          )}
          {ev.context && Object.keys(ev.context).length > 0 && (
            <pre className="max-w-full overflow-x-auto whitespace-pre-wrap break-words rounded bg-slate-950 p-2 text-[10px] text-slate-300">
              {JSON.stringify(ev.context, null, 2)}
            </pre>
          )}
          {ev.hasScreenshot && ev.screenshotId && (
            <DiagnosticScreenshot id={ev.screenshotId} />
          )}
        </div>
      )}
    </div>
  );
}

function DiagLevelBadge({ level }: { level: string }): JSX.Element {
  const cls =
    level === "error"
      ? "bg-rose-500/15 text-rose-200 ring-rose-400/30"
      : level === "warn"
      ? "bg-amber-500/15 text-amber-200 ring-amber-400/30"
      : "bg-slate-700/30 text-slate-300 ring-slate-600/40";
  return <span className={`pill ring-1 ${cls}`}>{level}</span>;
}

/**
 * Lazily fetches a diagnostic screenshot as an authenticated blob (the JWT
 * goes through the axios interceptor) and renders it via an object URL. We
 * can't use a plain `<img src>` because the endpoint requires the bearer
 * token, which a raw browser image request would not send.
 */
function DiagnosticScreenshot({ id }: { id: string }): JSX.Element {
  const { t } = useTranslation();
  const q = useQuery({
    queryKey: ["admin", "diagnostics", "screenshot", id],
    queryFn: async () => {
      const res = await api.get(`/api/admin/diagnostics/screenshot/${id}`, {
        responseType: "blob",
      });
      return URL.createObjectURL(res.data as Blob);
    },
    staleTime: 5 * 60_000,
  });

  if (q.isLoading) {
    return <p className="text-xs text-slate-500">{t("common.loading")}</p>;
  }
  if (q.isError || !q.data) {
    return (
      <p className="text-xs text-rose-300">{t("admin.diagnostics.screenshotFailed")}</p>
    );
  }
  return (
    <a href={q.data} target="_blank" rel="noreferrer">
      <img
        src={q.data}
        alt={t("admin.diagnostics.screenshotAlt")}
        className="max-h-96 w-auto rounded-lg border border-slate-800"
      />
    </a>
  );
}

function JobStatusBadge({ status }: { status: string }): JSX.Element {
  const cls =
    status === "success"
      ? "bg-emerald-500/15 text-emerald-200 ring-emerald-400/30"
      : status === "running"
      ? "bg-brand-500/15 text-brand-200 ring-brand-400/30"
      : status === "queued"
      ? "bg-slate-700/30 text-slate-300 ring-slate-600/40"
      : status === "skipped"
      ? "bg-slate-700/20 text-slate-400 ring-slate-600/30"
      : "bg-rose-500/15 text-rose-200 ring-rose-400/30";
  return <span className={`pill ring-1 ${cls}`}>{status}</span>;
}

/* -------------------------------------------------------------------------- */
/*  Shared widgets                                                            */
/* -------------------------------------------------------------------------- */

function Stat({
  label,
  value,
  tone = "default",
}: {
  label: string;
  value: number | string;
  tone?: "default" | "amber" | "rose";
}): JSX.Element {
  const toneCls =
    tone === "amber"
      ? "border-amber-500/30 bg-amber-500/5 text-amber-100"
      : tone === "rose"
      ? "border-rose-500/30 bg-rose-500/5 text-rose-100"
      : "border-slate-800 bg-slate-950/40";
  return (
    <div className={`rounded-xl border p-4 ${toneCls}`}>
      <div className="text-[11px] uppercase tracking-wider text-slate-400">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }): JSX.Element {
  return <th className="px-3 py-2 font-medium">{children}</th>;
}

function Td({ children }: { children: React.ReactNode }): JSX.Element {
  return <td className="px-3 py-2 align-top">{children}</td>;
}

function SubBadge({ status }: { status: string | null }): JSX.Element {
  if (!status) return <span className="text-slate-600">—</span>;
  const cls =
    status === "active"
      ? "bg-emerald-500/15 text-emerald-200 ring-emerald-400/30"
      : status === "trialing"
      ? "bg-brand-500/15 text-brand-200 ring-brand-400/30"
      : status === "past_due"
      ? "bg-amber-500/15 text-amber-200 ring-amber-400/30"
      : "bg-slate-700/30 text-slate-300 ring-slate-600/40";
  return <span className={`pill ring-1 ${cls}`}>{status}</span>;
}

function IgStatusBadge({ status }: { status: string }): JSX.Element {
  const cls =
    status === "active"
      ? "bg-emerald-500/15 text-emerald-200 ring-emerald-400/30"
      : status === "paused" || status === "verifying"
      ? "bg-slate-700/30 text-slate-300 ring-slate-600/40"
      : status === "banned"
      ? "bg-rose-500/15 text-rose-200 ring-rose-400/30"
      : "bg-amber-500/15 text-amber-200 ring-amber-400/30";
  return <span className={`pill ring-1 ${cls}`}>{status}</span>;
}

function Paginator({
  page,
  total,
  pageSize,
  onPage,
}: {
  page: number;
  total: number;
  pageSize: number;
  onPage: (n: number) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div className="flex items-center justify-between text-xs text-slate-400">
      <span>
        {t("admin.pagination.summary", {
          page,
          totalPages,
          total,
        })}
      </span>
      <div className="flex gap-1">
        <button
          type="button"
          className="btn-ghost btn-sm"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
        >
          {t("admin.pagination.prev")}
        </button>
        <button
          type="button"
          className="btn-ghost btn-sm"
          disabled={page >= totalPages}
          onClick={() => onPage(page + 1)}
        >
          {t("admin.pagination.next")}
        </button>
      </div>
    </div>
  );
}

/**
 * ISO-2 country code → flag emoji. Browsers render the regional indicator
 * symbol pair as the country flag everywhere except old Windows (which
 * shows letters). Pure CSS / Unicode trick — no asset dependencies.
 */
function countryFlag(iso2: string | null | undefined): string {
  if (!iso2 || iso2.length !== 2) return "";
  const base = 127397; // 0x1F1E6 - 'A'.charCodeAt(0)
  const upper = iso2.toUpperCase();
  return String.fromCodePoint(upper.charCodeAt(0) + base, upper.charCodeAt(1) + base);
}
